import { getProductDetailsTemplate } from "@/api/template";
import ActionsPopover from "@/components/ActionsPopOver/ActionsPopOver";
import ActionsPopOverItem from "@/components/ActionsPopOver/ActionsPopOverItem";
import FakeLink from "@/components/FakeLink/FakeLink";
import FileDownloader from "@/components/FileDownloader/FileDownloader";
import FileUploader from "@/components/FileUploader/FileUploader";
import HelpButton from "@/components/HelpButton/HelpButton";
import PageButtonGroup from "@/components/PageButtonGroup/PageButtonGroup";
import { COLLECTING_BREADCRUMB, PageHeaderNew } from "@/components/PageHeader/PageHeaderNew";
import StatusIcon from "@/components/StatusIcon/StatusIcon";
import StatusTag from "@/components/StatusTag/StatusTag";
import PrimarySourceTag from "@/components/StatusTags/PrimarySourceTag/PrimarySourceTag";
import ProxyTag from "@/components/StatusTags/ProxyTag/ProxyTag";
import StatusTags from "@/components/StatusTags/StatusTags";
import UnrelatedTag from "@/components/StatusTags/UnrelatedTag";
import SwitchWithLabel from "@/components/SwitchWithLabel/SwitchWithLabel";
import Table, { Column } from "@/components/Table/Table";
import useTableStateUrl from "@/components/Table/hooks/useTableStateUrl";
import TableHeader from "@/components/TableHeader/TableHeader";
import TooltipLink from "@/components/TooltipLink/TooltipLink";
import YearSelector from "@/components/YearSelector/YearSelector";
import useAnalytics from "@/hooks/useAnalytics";
import { useCompanySettings } from "@/hooks/useCompanySettings";
import useTrackOnLoad from "@/hooks/useTrackPageview";
import useTranslation from "@/hooks/useTranslation";
import useUrlStateBoolean from "@/hooks/useUrlStateBoolean";
import { formatMass } from "@/pages/dashboard/utils/formatters";
import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  ExportOutlined,
  MoreOutlined,
  SubnodeOutlined,
} from "@ant-design/icons";
import { Button, Flex, message, Space, Tooltip } from "antd";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useDashboardYear } from "../../dashboard/hooks/useDateRange";
import DeleteProductAction from "../actions/DeleteProductAction/DeleteProductAction";
import EditProductModal from "../actions/EditProductAction/EditProductAction";
import ManageProxyAction from "../actions/ManageProxyAction/ManageProxyAction";
import {
  useExportProductDetails,
  useGetProducts,
  usePatchProducts,
  useProductStatusPerYear,
} from "../useProducts";
import styles from "./ProductsOverview.module.scss";

export type ProductStatus = "complete" | "incomplete" | "out_of_scope";

export interface Product {
  id: number;
  name: string;
  category: string | null;
  external_id: string;
  mass: number | null;
  bom_mass: number | null;
  total_mass: number | null;
  packaging_ids: number[];
  external_packaging_ids: number[];
  relation: ("primary_source" | "proxy" | "unrelated")[];
  status: ProductStatus;
  bom_status: ProductStatus;
  eol_status: ProductStatus;
  usage_status: ProductStatus;
  mass_status: ProductStatus;
  packaging_status: ProductStatus;
  deletable: boolean;
  is_out_of_scope: boolean;
  proxy_id: number | undefined;
  proxy_weight: number | undefined;
  has_packaging: boolean;
}

export const proxyColumn: Column<Product> = {
  key: "relation",
  label: "Relation",
  minWidth: "170px",
  noLink: true,
  renderer: (product) => (
    <Link to={`/products/${product.id}/relations`}>
      <Space>
        {product.relation.includes("proxy") && <ProxyTag />}
        {product.relation.includes("primary_source") && <PrimarySourceTag />}
        {product.relation.includes("unrelated") && (
          <Tooltip title="Not related to purchase or sales orders">
            <>
              <UnrelatedTag />
            </>
          </Tooltip>
        )}
      </Space>
    </Link>
  ),
  filterable: true,
};

export default function ProductsOverview() {
  useTrackOnLoad("Products Viewed");

  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const exportProduct = useExportProductDetails();
  const patchProductsInScope = usePatchProducts();
  const patchProductsOutScope = usePatchProducts();
  const analytics = useAnalytics();
  const translation = useTranslation();
  const { year } = useDashboardYear();
  const [showUnrelated, setShowUnrelated] = useUrlStateBoolean("showUnrelated", false);

  const deselectUndeletableProducts = () => {
    setSelectedProducts((selectedProducts) => [...selectedProducts.filter((x) => x.deletable)]);
  };

  const companySettings = useCompanySettings();

  const columns: Column<Product>[] = [
    {
      key: "external_id",
      label: "ID",
      searchable: true,
      sortable: true,
    },
    {
      key: "name",
      label: "Product",
      maxWidth: "3fr",
      minWidth: "160px",
      sortable: true,
      searchable: true,
    },
    {
      key: "category",
      label: "Category",
      minWidth: "168px",
      sortable: true,
      searchable: true,
      renderer: (product) => product.category || "-",
    },
    proxyColumn,
    {
      key: "total_mass",
      label: "Mass",
      minWidth: "120px",
      maxWidth: "120px",
      sortable: true,
      noLink: true,
      renderer: (product) => (
        <Link
          to={`/products/${product.id}`}
          style={{ color: "inherit" }}
        >
          <Flex gap={8}>
            <StatusIcon status={product.mass_status} />
            {product.total_mass ? formatMass(product.total_mass) : "-"}
          </Flex>
        </Link>
      ),
    },
    {
      key: "packaging_status",
      label: "Packaging",
      minWidth: "160px",
      maxWidth: "160px",
      noLink: true,
      renderer: (product) => (
        <Link
          to={`/products/${product.id}/packaging`}
          style={{ color: "inherit" }}
        >
          <Flex gap={8}>
            <StatusIcon status={product.packaging_status} />
            {!product.has_packaging ? (
              "No"
            ) : product.external_packaging_ids.length === 0 ? (
              "-"
            ) : (
              <Tooltip title={product.external_packaging_ids.join(", ")}>
                {product.external_packaging_ids.length} packaging
              </Tooltip>
            )}
          </Flex>
        </Link>
      ),
      filterable: true,
    },
    {
      key: "bom_status",
      label: "BOM",
      minWidth: "120px",
      maxWidth: "120px",
      noLink: true,
      renderer: (product) => (
        <StatusIcon
          to={`/products/${product.id}/bom`}
          status={product.bom_status}
        />
      ),
      filterable: true,
    },
    ...(companySettings.data?.include_usage
      ? [
          {
            key: "usage_status",
            label: "Usage",
            minWidth: "130px",
            maxWidth: "130px",
            noLink: true,
            renderer: (product: Product) => (
              <StatusIcon
                to={`/products/${product.id}/usage`}
                status={product.usage_status}
              />
            ),
            filterable: true,
          },
        ]
      : []),
    ...(companySettings.data?.include_eol
      ? [
          {
            key: "eol_status",
            label: "End of life",
            minWidth: "160px",
            maxWidth: "160px",
            noLink: true,
            renderer: (product: Product) => (
              <StatusIcon
                to={`/products/${product.id}/eol`}
                status={product.eol_status}
              />
            ),
            filterable: true,
          },
        ]
      : []),
    {
      key: "status",
      label: "Status",
      minWidth: "150px",
      maxWidth: "150px",
      renderer: (product) => (
        <StatusTag
          showIcon={false}
          to={`/products/${product.id}`}
          status={product.status}
        />
      ),
      filterable: true,
      noLink: true,
    },
    {
      key: "actions",
      label: "Actions",
      minWidth: "80px",
      maxWidth: "80px",
      renderer: (product) => (
        <ActionsPopover
          actions={
            <>
              <EditProductModal
                productId={product.id}
                customRenderer={(props) => (
                  <ActionsPopOverItem
                    {...props}
                    icon={<EditOutlined />}
                  >
                    Edit product details
                  </ActionsPopOverItem>
                )}
              />
              <DeleteProductAction
                deselectUndeletableProducts={deselectUndeletableProducts}
                onSucces={() => {
                  setSelectedProducts([]);
                  analytics.track("Product Deleted");
                }}
                products={[product]}
              >
                <>
                  <ActionsPopOverItem
                    disabled={!product.deletable}
                    icon={<DeleteOutlined />}
                    danger
                  >
                    Delete product
                  </ActionsPopOverItem>
                </>
              </DeleteProductAction>
            </>
          }
        >
          <MoreOutlined />
        </ActionsPopover>
      ),
      align: "end",
    },
  ];

  const { pagination, filter, search, sort, onSearchFilterSortPaginate, setPage } =
    useTableStateUrl({
      columns,
      defaultSort: { key: "external_id", order: "ascend" },
    });
  const { data, isLoading, isFetching } = useGetProducts({
    year,
    showUnrelated,
    filter,
    sort,
    search,
    pagination,
  });

  const { data: productsYears } = useProductStatusPerYear();

  const onSelectedKeysChange = (keys: number[]) => {
    analytics.track("Product Selected");
    setSelectedProducts((currentProducts) => {
      let productsOnOtherPages = [
        ...currentProducts.filter((x) => !data?.data.map((x) => x.id).includes(x.id)),
      ];
      return [
        ...productsOnOtherPages,
        ...(data?.data.filter((product) => keys.includes(product.id)) || []),
      ];
    });
  };

  const addProxyDisabled = selectedProducts.some((product) =>
    product.relation.includes("primary_source")
  );

  const resetTable = () => {
    setPage(1);
    setSelectedProducts([]);
  };

  const productStatus = [
    { label: "Complete", value: "complete" },
    { label: "Incomplete", value: "incomplete" },
    { label: "Out of scope", value: "out_of_scope" },
  ];

  return (
    <div className={styles.ProductsOverview}>
      <PageHeaderNew
        title="Products"
        description="Here you find a list of all products detected in your purchase and sales orders. Upload and manage the bill of materials (BOM) for each product, individually or in bulk."
        breadcrumbs={[COLLECTING_BREADCRUMB]}
        breadcrumbsRight={
          <>
            <HelpButton
              size="small"
              type="link"
              url="https://help.root-sustainability.com/en/articles/9898491-products"
            />
          </>
        }
        titleRight={
          <PageButtonGroup>
            <FileUploader
              uploadType={"product_details"}
              uploadDataTableProps={{
                columns: [
                  { key: "id", label: "ID" },
                  { key: "name", label: "Name" },
                  { key: "category", label: "Category" },
                  { key: "mass", label: "Mass (kg)" },
                  { key: "packaging_ids", label: "Packaging IDs" },
                  { key: "proxy_id", label: "Proxy ID" },
                  { key: "proxy_multiplier", label: "Proxy multiplier" },
                  { key: "is_out_of_scope", label: "Out of scope" },
                ],
                rowKey: "id",
              }}
            />
            <FileDownloader
              fileOutputName={`template-product-details.xlsx`}
              fileUrlFn={getProductDetailsTemplate}
              buttonText="Template"
              icon={<DownloadOutlined />}
              onDownload={() => analytics.track("Product Template Downloaded")}
            />
            <FileDownloader
              tooltipText="Export"
              fileOutputName={`export-products.xlsx`}
              fileUrlFn={() => exportProduct.mutateAsync({ year }).then((data) => data.url)}
              buttonText={"Export"}
              icon={<ExportOutlined />}
              onDownload={() => analytics.track("Products Exported")}
              addTimestampAndCompany={true}
            />
          </PageButtonGroup>
        }
      />
      <TableHeader
        noRoundedTop
        left={
          <>
            {selectedProducts.length > 0 ? (
              <span className={styles["num-selected"]}>{selectedProducts.length} selected:</span>
            ) : (
              <YearSelector
                disableYears={showUnrelated}
                optionData={productsYears}
                onChange={resetTable}
                right={
                  <Tooltip
                    placement="bottom"
                    title="Show products that are not related to purchase or sales orders"
                  >
                    <>
                      <SwitchWithLabel
                        togglePosition="right"
                        size="small"
                        checked={showUnrelated}
                        onChange={(disabled) => {
                          resetTable();
                          setShowUnrelated(disabled);
                        }}
                      >
                        Year unrelated
                      </SwitchWithLabel>
                    </>
                  </Tooltip>
                }
              />
            )}
            <PageButtonGroup>
              {selectedProducts.length > 0 ? (
                <>
                  <ManageProxyAction
                    state="edit"
                    disabled={addProxyDisabled}
                    proxyIds={selectedProducts.map((product) => product.id)}
                    multiple={true}
                    deletable={true}
                  >
                    <Tooltip
                      title={
                        addProxyDisabled && (
                          <>
                            You can not add a proxy to these products because one of the products
                            you selected is a source to other proxies.{" "}
                            <TooltipLink
                              onClick={() =>
                                setSelectedProducts(
                                  selectedProducts.filter((product) =>
                                    product.relation.includes("proxy")
                                  )
                                )
                              }
                            >
                              Click here
                            </TooltipLink>{" "}
                            to deselect or deselect them manually in order to continue.
                          </>
                        )
                      }
                    >
                      <Button disabled={addProxyDisabled}>
                        <SubnodeOutlined /> Manage proxy
                      </Button>
                    </Tooltip>
                  </ManageProxyAction>
                  <Button
                    disabled={patchProductsInScope.isLoading}
                    loading={patchProductsInScope.isLoading}
                    onClick={() =>
                      patchProductsInScope
                        .mutateAsync({
                          productIds: selectedProducts.map((product) => product.id),
                          patch: { is_out_of_scope: false },
                        })
                        .catch(() => message.error(translation.errors.general))
                    }
                  >
                    Include in scope
                  </Button>
                  <Button
                    disabled={patchProductsOutScope.isLoading}
                    loading={patchProductsOutScope.isLoading}
                    onClick={() =>
                      patchProductsOutScope
                        .mutateAsync({
                          productIds: selectedProducts.map((product) => product.id),
                          patch: { is_out_of_scope: true },
                        })
                        .catch(() => message.error(translation.errors.general))
                    }
                  >
                    Exclude from scope
                  </Button>
                  <DeleteProductAction
                    deselectUndeletableProducts={deselectUndeletableProducts}
                    products={selectedProducts}
                    onSucces={() => {
                      setSelectedProducts([]);
                      analytics.track("Products Deleted");
                    }}
                  >
                    <Button disabled={selectedProducts.some((x) => !x.deletable)}>
                      <DeleteOutlined style={{ color: "#F5222D" }} />
                    </Button>
                  </DeleteProductAction>
                </>
              ) : (
                <>
                  <ManageProxyAction
                    multiple={true}
                    state="add"
                  >
                    <Button>
                      <SubnodeOutlined /> Add proxy
                    </Button>
                  </ManageProxyAction>
                </>
              )}
            </PageButtonGroup>
            {selectedProducts.length > 0 && (
              <FakeLink onClick={() => setSelectedProducts([])}>Clear selection</FakeLink>
            )}
          </>
        }
        right={
          <StatusTags
            data={data}
            loading={isLoading}
          />
        }
      />
      <Table<Product>
        availableFilters={{
          status: productStatus,
          bom_status: productStatus,
          eol_status: productStatus,
          usage_status: productStatus,
          mass_status: productStatus,
          packaging_status: productStatus,
          relation: [
            { label: "Source", value: "primary_source" },
            { label: "Proxy", value: "proxy" },
            { label: "Unrelated", value: "unrelated" },
          ],
        }}
        loading={isLoading || isFetching}
        rowKey="id"
        checkable={true}
        data={data?.data}
        onSearchFilterSortPaginate={onSearchFilterSortPaginate}
        onSelectedKeysChange={onSelectedKeysChange}
        selectedKeys={selectedProducts.map((x) => x.id)}
        totalRows={data?.total_count}
        pagination={pagination}
        sort={sort}
        search={search}
        filter={filter}
        columns={columns}
        rowLink={(record) => `/products/${record.id}`}
      />
    </div>
  );
}
